import {Box, IconButton, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import CakeOutlined from "@mui/icons-material/CakeOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import {useDraggable} from "@dnd-kit/core";
import {useTranslation} from "react-i18next";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {getProfileFullNameReversed} from "crqlar-react-common/dist/utils/profile.util";
import {
    getIconForLanguage,
    getNameForLanguage
} from "crqlar-react-common/dist/utils/translations.util";
import {AgeCategoriesElement} from "../../../../../common/uiElements/AgeCategoriesElement";
import {
    ALLERGY_ICON_MAP,
    getFloorplanGuestCounts,
    getOrderedFloorplanReservationProfiles,
    getPrimaryProfileId,
    getReservationDisplay,
} from "../../utils/reservation-utils";
import {DragData} from "../../types";
import {TABLE_RESERVATION_COLUMN_WIDTHS} from "./tableReservationDialogLayout";

interface Props {
    reservation: FloorplanReservationItemDTO;
    tableId: string;
    onClickEdit: () => void;
}

const DraggableReservationRow: React.FC<Props> = ({
                                                      reservation,
                                                      tableId,
                                                      onClickEdit
                                                  }) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const displayGuestCounts = getFloorplanGuestCounts(reservation);

    const {attributes, listeners, setNodeRef, isDragging} = useDraggable({
        id: `dialog-${tableId}-${reservation.reservationId}`,
        data: {
            type: "assigned",
            reservation,
            sourceTableId: tableId,
            origin: "table-reservations-dialog",
        } as DragData,
    });

    const {
        guestNames,
        guestCount,
        timeRange,
        statusText,
        status,
        backgroundColor,
        borderColor,
        end,
        primaryLanguage,
        roomNames,
        roomNamesInline,
        fromBookingEngine,
        birthdayNames,
        isBirthday,
        preferences,
    } = getReservationDisplay({
        reservation: reservation.restaurantReservation,
        profiles: reservation.profiles,
        ageCategories: reservation.ageCategories,
        accommodationResources: reservation.accommodationResources,
        guestCounts: displayGuestCounts,
    });

    const windowStart = new Date();
    const isPast = end ? end < windowStart : false;
    const restaurantReservation = reservation.restaurantReservation;
    const primaryProfileId = getPrimaryProfileId(restaurantReservation)
        ?? reservation.profiles[0]?.id;
    const displayedProfiles = getOrderedFloorplanReservationProfiles(reservation);
    const hasPrimaryProfile = displayedProfiles.some(profile => profile.id === primaryProfileId);
    const profileNames = displayedProfiles
        .map((profile, index) => ({
            key: profile.id ?? `${index}`,
            name: getProfileFullNameReversed(profile, ""),
            isPrimary: hasPrimaryProfile
                ? profile.id === primaryProfileId
                : index === 0,
        }))
        .filter(profile => Boolean(profile.name));
    const preferenceGroups = Array.from(new Set(
        preferences
            .map(preference => preference.preferenceType)
            .filter((type): type is string => Boolean(type)),
    )).flatMap(type => {
        const mapping = ALLERGY_ICON_MAP[type];
        if (!mapping) return [];

        const values = Array.from(new Set(
            preferences
                .filter(preference => preference.preferenceType === type)
                .map(preference => preference.preferenceValue?.trim())
                .filter((value): value is string => Boolean(value)),
        ));
        if (!values.length) return [];

        return [{
            type,
            Icon: mapping.icon,
            color: mapping.color,
            tooltip: values.join(", "),
        }];
    });

    return (
        <Box
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            display="flex"
            alignItems="center"
            px={1}
            py={1}
            sx={{
                borderBottom: "1px solid #ccc",
                bgcolor: isDragging ? "rgba(0,0,0,0.03)" : "transparent",
                transition: "background-color 150ms ease-in-out",
                "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                },
            }}
            style={{
                opacity: (isDragging || isPast) ? 0.5 : 1,
                touchAction: "manipulation",
            }}
        >
            <Box sx={{flex: 1, minWidth: 0}}>
                {profileNames.length > 0 ? (
                    <Stack gap={0.25} sx={{whiteSpace: "normal"}}>
                        {profileNames.map(profile => (
                            <Box
                                key={profile.key}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    minWidth: 0,
                                }}
                            >
                                {profile.isPrimary && primaryLanguage && (
                                    <Box
                                        component="img"
                                        alt={getNameForLanguage(primaryLanguage)}
                                        src={getIconForLanguage(primaryLanguage)}
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "0.5px solid rgba(0,0,0,0.15)",
                                            flex: "0 0 auto",
                                        }}
                                    />
                                )}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: profile.isPrimary ? 600 : 400,
                                        lineHeight: 1.35,
                                        minWidth: 0,
                                    }}
                                >
                                    {profile.name}
                                </Typography>
                                {profile.isPrimary && fromBookingEngine && (
                                    <Tooltip title={t("Online Reservation")} arrow>
                                        <BookOnlineIcon
                                            sx={{
                                                fontSize: 14,
                                                color: "text.secondary",
                                                flex: "0 0 auto",
                                            }}
                                        />
                                    </Tooltip>
                                )}
                                {profile.isPrimary && isBirthday && (
                                    <Tooltip
                                        title={birthdayNames
                                            .map(name => t("It's {{name}}'s birthday", {name}))
                                            .join(", ")}
                                        arrow
                                    >
                                        <CakeOutlined
                                            sx={{
                                                fontSize: 14,
                                                color: "#9c27b0",
                                                flex: "0 0 auto",
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body1">
                        {guestNames || "—"}
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    width: TABLE_RESERVATION_COLUMN_WIDTHS.ageCategories,
                    flexShrink: 0,
                    alignSelf: "center",
                }}
            >
                {guestCount > 0 ? (
                    <AgeCategoriesElement
                        ageCategories={reservation.ageCategories}
                        ageCategoryCounts={displayGuestCounts}
                        aliasAgeCategories={reservation.aliasAgeCategories}
                        totalGuests={guestCount}
                        variant="inline"
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        —
                    </Typography>
                )}
            </Box>
            <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                title={roomNames || undefined}
                sx={{
                    width: TABLE_RESERVATION_COLUMN_WIDTHS.room,
                    flexShrink: 0,
                    alignSelf: "center",
                    pr: 1,
                }}
            >
                {roomNamesInline || "—"}
            </Typography>
            <Box
                sx={{
                    width: TABLE_RESERVATION_COLUMN_WIDTHS.preferences,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                }}
            >
                {preferenceGroups.length > 0 ? (
                    preferenceGroups.map(({type, Icon, color, tooltip}) => (
                        <Tooltip key={type} title={tooltip} arrow>
                            <Icon
                                sx={{
                                    fontSize: 17,
                                    color,
                                    cursor: "default",
                                }}
                            />
                        </Tooltip>
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        —
                    </Typography>
                )}
            </Box>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    width: TABLE_RESERVATION_COLUMN_WIDTHS.status,
                    flexShrink: 0,
                    textAlign: "left",
                    alignSelf: "center",
                }}
            >
                <Box sx={{display: "inline-flex", alignItems: "center", gap: 0.5}}>
                    <Box
                        sx={{
                            width: 10,
                            height: 24,
                            borderRadius: 0.5,
                            bgcolor: backgroundColor,
                            border: "1px solid",
                            borderColor,
                        }}
                    />
                    {status ? t(`Reservation status ${status}`, {defaultValue: statusText}) : statusText}
                </Box>
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    width: TABLE_RESERVATION_COLUMN_WIDTHS.time,
                    flexShrink: 0,
                    textAlign: "left",
                    alignSelf: "center",
                }}
            >
                {timeRange}
            </Typography>
            <Box
                sx={{
                    width: TABLE_RESERVATION_COLUMN_WIDTHS.actions,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                }}
            >
                <IconButton
                    aria-label={t("Edit reservation")}
                    size="small"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                        event.stopPropagation();
                        onClickEdit();
                    }}
                >
                    <EditIcon fontSize="small"/>
                </IconButton>
                <IconButton
                    aria-label={t("Drag")}
                    edge="end"
                    size="small"
                    sx={{cursor: isDragging ? "grabbing" : "grab"}}
                >
                    <DragIndicatorIcon fontSize="small"/>
                </IconButton>
            </Box>
        </Box>
    );
};

export default DraggableReservationRow;
